import axios from "axios";
import { Skeleton, Tooltip } from "@chakra-ui/react";
import moment from "moment";
import { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { useSelector } from "react-redux";
import { FiSettings } from "react-icons/fi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import phone from "../../assets/svgs/call.svg";
import Plus from "../../assets/svgs/Plus.svg";
import addContactIcon from "../../assets/svgs/add-contact.svg";
import sortAsce from "../../assets/svgs/sort-ascending.svg";
import sortDesc from "../../assets/svgs/sort-descending.svg";
import swap from "../../assets/svgs/swap-vertical.svg";
import ImportExport from "../../components/ImportExport";
import Loader from "../../components/Loader";
import DataAsOfNotice from "../../components/DataAsOfNotice";
import { ROUTES } from "../../routes/routeKeys";
import ContactPagination from "../../components/Pagination/ContactPagination";
import PhoneStatusModal from "../../components/PhoneStatusModal";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import { BASE_URL } from "../../utils/Element";
import { initialContactFilterData } from "../../utils/initialData";
import { formatPhoneNumber } from "../../utils/utils";
import ActivityModal from "./components/ActivityModal";
import AddContact from "./components/AddContact";

import ColumnCustomizer from "../../components/ColumnCustomizer";
import ContactAdvanceFilter from "./components/ContactAdvanceFilter";
import "./Contact.css";

// Custom debounce hook
const useDebounce = (value, delay = 600) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const Contacts = () => {
  const [config] = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(50);
  const [contactsData, setContactsData] = useState([]);
  const [selectedItem, setSelectedItem] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState("");
  const [phoneNumber, setPhoneNumber] = useState('');

  const [sortBy, setSortBy] = useState("updated_at");
  const [sortDirection, setSortDirection] = useState("desc");

  // New state for include_latest_activity
  const [includeLatestActivity, setIncludeLatestActivity] = useState(false);

  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData");
    return localFilterData ? JSON.parse(localFilterData) : initialContactFilterData;
  });

  const [advanceFilterData, setAdvanceFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData");
    return localFilterData ? JSON.parse(localFilterData) : [];
  });

  // Column Customization
  const allColumns = [
    { key: 'select', label: 'Select', fixed: true },
    { key: 'contact_name', label: 'Contact Name', fixed: true },
    { key: 'company_name', label: 'Company Name' },
    { key: 'last_contacted', label: 'Last Contacted' },
    { key: 'primary_phone', label: 'Primary Phone' },
    { key: 'primary_email', label: 'Primary Email' },
    { key: 'tags', label: 'Tags' },
  ];

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('contactVisibleColumns');
    return saved ? JSON.parse(saved) : allColumns.map(col => col.key);
  });

  const queryParams = new URLSearchParams(location.search);
  const [searchInput, setSearchInput] = useState(queryParams.get('search') ?? '');
  const debouncedSearch = useDebounce(searchInput, 600);
  const [search, setSearch] = useState(queryParams.get('search') ?? '');

  const contactAllIds = contactsData?.map((el) => el.id);

  // ==================== BUILD QUERY PARAMS ====================
  const buildQueryParams = (filters, includeLatest = false) => {
    let params = `page=${currentPage}&per_page=${itemPerPage}`;

    if (search) {
      const encodedSearch = encodeURIComponent(search);
      params += `&search=${encodedSearch}`;
    }

    const {
      company, property, first_name, last_name, email, interaction_type_user,
      phone, street_address, city, state, zip_code, interaction_type, count,
      start_count, end_count, leadsource_id, website, first_deal_anniversary,
      has_acquisition, tax_record_sent, tax_record_sent_date, tax_record_start_date,
      tax_record_end_date, tags, withoutTags, tag_category, last_contact,
      lastContactSentDate, lastContactSentEdate, lastContactSentSdate,
      date_category, within_last_day, longer_than_day, activity_end_count,
      activity_start_count, activity_count
    } = filters;

    // Existing filter parameters
    if (company?.id) params += `&company=${encodeURIComponent(company.id)}`;
    if (property) params += `&property=${encodeURIComponent(property)}`;
    if (interaction_type) params += `&interaction_type=${encodeURIComponent(interaction_type)}`;
    if (first_name) params += `&first_name=${encodeURIComponent(first_name)}`;
    if (last_name) params += `&last_name=${encodeURIComponent(last_name)}`;
    if (email) params += `&email=${encodeURIComponent(email)}`;
    if (phone) params += `&phone=${encodeURIComponent(phone)}`;
    if (street_address) params += `&street_address=${encodeURIComponent(street_address)}`;
    if (city) params += `&city=${encodeURIComponent(city)}`;
    if (state) params += `&state=${encodeURIComponent(state)}`;
    if (zip_code) params += `&zip_code=${encodeURIComponent(zip_code)}`;
    if (interaction_type_user) params += `&interaction_type_user=${encodeURIComponent(interaction_type_user)}`;
    if (leadsource_id) params += `&leadsource_id=${encodeURIComponent(leadsource_id)}`;
    if (website !== undefined) params += `&website=${encodeURIComponent(website)}`;
    if (first_deal_anniversary !== undefined) params += `&first_deal_anniversary=${encodeURIComponent(first_deal_anniversary)}`;
    if (has_acquisition !== undefined) params += `&has_acquisition=${encodeURIComponent(has_acquisition)}`;
    if (tax_record_sent !== undefined) params += `&tax_record_sent=${encodeURIComponent(tax_record_sent)}`;
    if (tax_record_sent_date) params += `&tax_record_sent_date=${encodeURIComponent(tax_record_sent_date)}`;
    if (tax_record_start_date) params += `&tax_record_start_date=${encodeURIComponent(tax_record_start_date)}`;
    if (tax_record_end_date) params += `&tax_record_end_date=${encodeURIComponent(tax_record_end_date)}`;

    if (tag_category && tags?.length > 0) params += `&tag_category=${encodeURIComponent(tag_category)}`;
    if (withoutTags?.length > 0) params += `&withoutTags=${encodeURIComponent(withoutTags.join(","))}`;
    if (tags?.length > 0) params += `&tags=${encodeURIComponent(tags.join(","))}`;

    if (last_contact) {
      params += `&last_contact=${encodeURIComponent(last_contact)}`;
    }
    if (lastContactSentDate) params += `&last_update_date=${encodeURIComponent(lastContactSentDate)}`;
    if (lastContactSentSdate) params += `&last_update_start_date=${encodeURIComponent(lastContactSentSdate)}`;
    if (lastContactSentEdate) params += `&last_update_end_date=${encodeURIComponent(lastContactSentEdate)}`;

    if (date_category !== undefined) params += `&date_category=${encodeURIComponent(date_category)}`;
    if (within_last_day) params += `&within_last_day=${encodeURIComponent(within_last_day)}`;
    if (longer_than_day) params += `&longer_than_day=${encodeURIComponent(longer_than_day)}`;
    if (count) params += `&count=${encodeURIComponent(count)}`;
    if (start_count) params += `&start_count=${encodeURIComponent(start_count)}`;
    if (end_count) params += `&end_count=${encodeURIComponent(end_count)}`;
    if (activity_count) params += `&activity_count=${encodeURIComponent(activity_count)}`;
    if (activity_start_count) params += `&activity_start_count=${encodeURIComponent(activity_start_count)}`;
    if (activity_end_count) params += `&activity_end_count=${encodeURIComponent(activity_end_count)}`;

    // Sorting
    if (sortDirection && sortBy !== "id") {
      params += `&sort_direction=${sortDirection}`;
    }
    if (sortBy !== "id") {
      params += `&sort_by=${sortBy}`;
    }

    // Include Latest Activity
    params += `&include_latest_activity=${includeLatest}`;

    localStorage.setItem("queryParams", params);
    return params;
  };

  const handleSelectCheck = (item) => {
    const isSelected = selectedItem.some((el) => el.id === item.id);
    if (isSelected) {
      setSelectedItem(selectedItem.filter((el) => el.id !== item.id));
    } else {
      setSelectedItem([...selectedItem, item]);
    }
  };

  useEffect(() => {
    if (selectedItem.length === contactAllIds.length && contactAllIds.length > 0) {
      setIsSelectAll(true);
    } else {
      setIsSelectAll(false);
    }
  }, [selectedItem, contactAllIds]);

  const fetchContacts = (filters, advanceFilters = [], includeLatest = false) => {
    const queryParams = buildQueryParams(filters, includeLatest);
    const validAdvanceFilters = advanceFilters.filter(group =>
      group.filters && group.filters.some(f => f.field && f.operator)
    );
    const advanceFiltersQuery = encodeURIComponent(JSON.stringify(validAdvanceFilters));

    setLoading(true);

    axios
      .get(`${BASE_URL}/contact-list?${queryParams}&advance_filters=${advanceFiltersQuery}`, config)
      .then((res) => {
        const value = res?.data?.data?.contacts;
        setContactsData(value?.data || []);
        setPaginationData({
          totalItems: value?.total ?? 0,
          from: value?.from ?? 0,
          to: value?.to ?? 0,
          totalPages: value?.last_page ?? 0,
        });
        setSelectedItem([]);
        setIsSelectAll(false);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  const [paginationData, setPaginationData] = useState({ totalItems: 0, from: 0, to: 0, totalPages: 0 });

  const onSuccess = (filters, advanceFilters, includeLatest = includeLatestActivity) => {
    const normalFilters = filters || filterData;
    const advFilters = advanceFilters !== undefined ? advanceFilters : advanceFilterData;
    fetchContacts(normalFilters, advFilters, includeLatest);
  };

  // Effects
  useEffect(() => {
    fetchContacts(filterData, advanceFilterData, includeLatestActivity);
  }, [sortBy, sortDirection, includeLatestActivity]);

  useEffect(() => {
    onSuccess(filterData, advanceFilterData, includeLatestActivity);
  }, [currentPage, itemPerPage, search, includeLatestActivity]);

  useEffect(() => {
    if (debouncedSearch.length >= 3 || debouncedSearch === '') {
      setSearch(debouncedSearch);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (search.trim()) {
      params.set('search', search);
    } else {
      params.delete('search');
    }
    navigate({ search: params.toString() }, { replace: true });
  }, [search, navigate, location.search]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const trimmed = newValue.startsWith(" ") ? newValue.trimStart() : newValue;
    setSearchInput(trimmed);
  };

  const handleNext = () => {
    if (currentPage !== paginationData.totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

const loadRingCentralScript = () => { 
    return new Promise((resolve, reject) => {
      if (document.getElementById('rc-embeddable-script')) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      const clientId = "VXh8hvv1uEjfhbIh4G3MSV";
      const appServer = "https://platform.ringcentral.com";
      const appSecret = 'XtuWDyDsPcnfhi8iFqk7Pv5vU6VrPrnT1fDIiwdeA42d';
      const redirectUri = encodeURIComponent('https://apps.ringcentral.com/integration/ringcentral-embeddable/latest/redirect.html');
      script.id = 'rc-embeddable-script';
      script.src = `https://apps.ringcentral.com/integration/ringcentral-embeddable/latest/adapter.js?clientId=${clientId}&appServer=${appServer}&appSecret=${appSecret}&redirectUri=${redirectUri}`;
      script.onload = resolve;
      script.onerror = () => reject(new Error('Failed to load RingCentral Embeddable script'));
      document.body.appendChild(script);
    });
  };

  const initializeWidget = () => { /* ... same ... */
    const existingWidget = document.getElementById('rc-widget');
    if (existingWidget) {
      existingWidget.style.display = 'none';
      return;
    }
    const widgetContainer = document.createElement('div');
    widgetContainer.id = 'rc-widget';
    widgetContainer.style.display = 'none';
    document.body.appendChild(widgetContainer);

    if (window.RingCentralEmbeddable && typeof window.RingCentralEmbeddable.createWidget === 'function') {
      window.RingCentralEmbeddable.createWidget({
        appKey: 'VXh8hvv1uEjfhbIh4G3MSV',
        server: 'https://platform.ringcentral.com',
        domElement: widgetContainer,
      });
    }
  };

  const waitForIframeAndSendCall = (phoneNumber) => {
    const iframe = document.querySelector('iframe');
    if (iframe) {
      iframe.contentWindow.postMessage({
        type: 'rc-adapter-new-call',
        phoneNumber: phoneNumber,
        callWithRingCentral: true,
      }, '*');
    } else {
      setTimeout(() => waitForIframeAndSendCall(phoneNumber), 500);
    }
  };

  const placeCall = (phoneNumber) => {
    const rcWidget = document.getElementById('rc-widget');
    if (!rcWidget) {
      loadRingCentralScript()
        .then(() => {
          initializeWidget();
          setTimeout(() => {
            const widget = document.getElementById('rc-widget');
            if (widget) widget.style.display = 'block';
            waitForIframeAndSendCall(phoneNumber);
          }, 2000);
        })
        .catch((error) => console.error(error.message));
    } else {
      rcWidget.style.display = 'block';
      waitForIframeAndSendCall(phoneNumber);
    }
  };

   const closeWidget = () => {
    const widget = document.getElementById('rc-widget');
    if (widget) widget.style.display = 'none';
  };

  return (
    <BaseLayout>
      <div className="contracts_only">
        <div className="contact-header">
          <p className="head-1 dark-H">
            Contacts
            {selectedItem?.length > 0 && (
              <span className="body-L ml-2">
                ({selectedItem?.length} <span className="dark-H">{selectedItem?.length === 1 ? "item" : "items"} selected</span>)
              </span>
            )}
          </p>

        </div>

        <div className="flex xl:flex-row flex-col justify-between items-center mb-4 mt-2 w-full gap-2 px-1 relative z-50">
          <div className="flex gap-2 items-center flex-wrap xl:flex-nowrap flex-1 w-full xl:w-auto">
            <div className="flex-shrink-0 whitespace-nowrap">
              <ContactAdvanceFilter
                filterData={advanceFilterData}
                onSetFilterData={setAdvanceFilterData}
                onCallApiAgain={(newAdvanceFilters) => {
                  setAdvanceFilterData(newAdvanceFilters);
                  fetchContacts(filterData, newAdvanceFilters, includeLatestActivity);
                }}
              />
            </div>

            <div className="flex-shrink-0 whitespace-nowrap">
              <ImportExport
                from="contact"
                onCallApi={() => {
                  onSuccess(filterData, advanceFilterData, includeLatestActivity);
                  setSelectedItem([]);
                }}
                filterData={filterData}
                advanceFilterData={advanceFilterData}
              />
            </div>

            <div className="search-box contacts relative w-[180px] xl:w-[220px] flex-shrink-1">
              <input
                type="text"
                className="body-S"
                placeholder="Search Here..."
                value={searchInput}
                onChange={handleInputChange}
                style={{ border: "1px solid #D8D8D8", marginTop: "0px", width: "100%" }}
              />
            </div>
          </div>

          <div className="flex gap-2 items-center flex-shrink-0 whitespace-nowrap">
            <button
              className="add-contact-button green-bg-H light-L body-S"
              onClick={() => {
                setIncludeLatestActivity(true);
                setShowActivityModal(true);
              }}
            >
              View Activity
            </button>

            <button
              className="add-contact-button green-bg-H light-L body-S flex items-center gap-2"
              onClick={() => setShowColumnModal(true)}
            >
              <FiSettings className="w-4 h-4" /> Columns
            </button>

            <button
              className="add-contact-button green-bg-H light-L body-S"
              onClick={() => setShowModal(true)}
            >
              <img className="mr-2 sidebar-icons" src={Plus} alt="plus" /> Add
            </button>
          </div>
        </div>

        <div className="light-bg-L pb-3 table-container bg-white rounded-xl border border-gray-200 overflow-hidden relative min-h-[400px]">
          {loading && (
            <div className="absolute inset-0 z-10 flex justify-center items-center bg-white/40 backdrop-blur-[1px]">
              <Loader />
            </div>
          )}
          <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
            <table className="contact-table two-rows-static light-bg-L">
                <thead>
                  <tr className="uppercase body-N dark-M">
                    {visibleColumns.includes('select') && (
                      <th>
                        <label className="container">
                          <input
                            type="checkbox"
                            checked={selectedItem.length > 0 && selectedItem.length === contactsData.length}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setIsSelectAll(isChecked);
                              if (isChecked) {
                                setSelectedItem([...contactsData]);
                              } else {
                                setSelectedItem([]);
                              }
                            }}
                          />
                          <span className="checkmark"></span>
                        </label>
                      </th>
                    )}

                    {visibleColumns.includes('contact_name') && (
                      <th className="green-H">
                        <div className="table-header">
                          Contact Name
                          <img
                            role="button"
                            src={sortBy !== "first_name" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("first_name");
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}

                    {visibleColumns.includes('company_name') && (
                      <th>
                        <div className="table-header">
                          Company Name
                          <img
                            role="button"
                            src={sortBy !== "company_name" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("company_name");
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}

                    {visibleColumns.includes('last_contacted') && (
                      <th>
                        <div className="table-header">
                          Last Contacted
                          <img
                            role="button"
                            src={sortBy !== "updated_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("updated_at");
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}

                    {visibleColumns.includes('primary_phone') && (
                      <th>
                        <div className="table-header">
                          PRIMARY PHONE
                          <img
                            role="button"
                            src={sortBy !== "phone" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("phone");
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}

                    {visibleColumns.includes('primary_email') && (
                      <th>
                        <div className="table-header">
                          PRIMARY EMAIL
                          <img
                            role="button"
                            src={sortBy !== "email" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("email");
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}

                    {visibleColumns.includes('tags') && (
                      <th>
                        <div className="table-header">
                          Tags
                          <img
                            role="button"
                            src={sortBy !== "tags" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("tags");
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    [...Array(10)].map((_, idx) => (
                      <tr key={idx} className="dark-H body-N">
                        {visibleColumns.includes('select') && <td><Skeleton height="20px" width="20px" /></td>}
                        {visibleColumns.includes('contact_name') && <td><Skeleton height="20px" width="120px" /></td>}
                        {visibleColumns.includes('company_name') && <td><Skeleton height="20px" width="140px" /></td>}
                        {visibleColumns.includes('last_contacted') && <td><Skeleton height="20px" width="100px" /></td>}
                        {visibleColumns.includes('primary_phone') && <td><Skeleton height="20px" width="120px" /></td>}
                        {visibleColumns.includes('primary_email') && <td><Skeleton height="20px" width="160px" /></td>}
                        {visibleColumns.includes('tags') && <td><Skeleton height="20px" width="80px" /></td>}
                      </tr>
                    ))
                  ) : contactsData?.length === 0 ? (
                    <tr style={{ border: "none" }} className="border-none">
                      <td colSpan={visibleColumns.length} className="body-N text-center mt-6 p-10 border-none" style={{ border: "none" }}>
                        <div className="flex flex-col items-center justify-center py-12">
                          <img src={addContactIcon} alt="No Contacts" className="w-16 h-16 mb-4 opacity-60" />
                          <p className="head-4 dark-H mb-2">No Contacts Found</p>
                          <p className="body-N dark-M mb-6">There are no contacts matching your search or filters.</p>
                          <button
                            className="add-contact-button green-bg-H light-L body-S flex items-center gap-2"
                            onClick={() => setShowModal(true)}
                          >
                            <img className="sidebar-icons" src={Plus} alt="plus" /> Add Contact
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    contactsData.map((el, idx) => (
                      <tr key={idx} className="dark-H body-N">
                      {visibleColumns.includes('select') && (
                        <td>
                          <label className="container">
                            <input
                              type="checkbox"
                              checked={selectedItem.some((item) => item.id === el.id)}
                              onChange={() => handleSelectCheck(el)}
                            />
                            <span className="checkmark"></span>
                          </label>
                        </td>
                      )}

                      {visibleColumns.includes('contact_name') && (() => {
                        const fullName = `${el.first_name || ""} ${el.last_name || ""}`.trim();
                        const displayFullName = fullName.length > 25 ? fullName.substring(0, 25) + "..." : fullName;
                        return (
                          <td className="head-5 normal-case">
                            <Tooltip label={fullName} bg="white" color="black" fontWeight="normal" hasArrow placement="top" isDisabled={fullName.length <= 25}>
                              <Link to={`${ROUTES.CONTACT_DETAILS.replace(":id", el.id)}?search=${search}`}>
                                {displayFullName}
                              </Link>
                            </Tooltip>
                          </td>
                        );
                      })()}

                      {visibleColumns.includes('company_name') && (() => {
                        const companyName = el?.company?.company_name || "";
                        const displayCompany = companyName.length > 25 ? companyName.substring(0, 25) + "..." : companyName;
                        return (
                          <td className="green-H head-5">
                            <Tooltip label={companyName} bg="white" color="black" fontWeight="normal" hasArrow placement="top" isDisabled={companyName.length <= 25}>
                              <Link
                                to={ROUTES.COMPANY_DETAILS.replace(":id", el.company_id)}
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (el?.company_id) navigate(ROUTES.COMPANY_DETAILS.replace(":id", el.company_id));
                                }}
                              >
                                {displayCompany}
                              </Link>
                            </Tooltip>
                          </td>
                        );
                      })()}

                      {visibleColumns.includes('last_contacted') && (
                        <td>
                          {el?.get_latest_activity?.date
                            ? moment(el.get_latest_activity.date).format("MM/DD/YY")
                            : ''}
                          <span className="dark-M body-S">
                            {el?.get_latest_activity?.date_connected
                              ? ` (${moment(el.get_latest_activity.date_connected).fromNow()})`
                              : ''}
                          </span>
                        </td>
                      )}

                      {visibleColumns.includes('primary_phone') && (
                        <td>
                          <div className="flex items-center gap-2">
                            {el.phone?.[0]?.phone_number ? (
                              <>
                                <img
                                  role="button"
                                  src={phone}
                                  alt="icon"
                                  className="sidebar-icons"
                                  onClick={() => placeCall(el.phone[0].phone_number)}
                                />
                                <span>{formatPhoneNumber(el.phone[0].phone_number)}</span>
                              </>
                            ) : ''}
                          </div>
                        </td>
                      )}

                      {visibleColumns.includes('primary_email') && (
                        <td>{el.email?.[0]?.email_id}</td>
                      )}

                      {visibleColumns.includes('tags') && (
                        <td className="flex flex-wrap items-center gap-2" style={{ width: "350px" }}>
                          {el?.tags?.map((tag, i) => (
                            <p key={i} className="tags green-H body-S">
                              {tag?.tag_name ? tag.tag_name : tag}
                            </p>
                          ))}
                        </td>
                      )}
                    </tr>
                  ))
                  )}
                </tbody>
              </table>
          </div>

          {contactsData.length !== 0 && (
            <ContactPagination
              from="contact"
              setSelectedItem={setSelectedItem}
              selectedItem={selectedItem}
              paginationData={paginationData}
              handleNext={handleNext}
              handlePrev={handlePrev}
              tags={tags}
              setTags={setTags}
              onSuccess={(filters) => onSuccess(filters, advanceFilterData)}
              itemPerPage={itemPerPage}
              handleItemPerPageSelect={(value) => {
                setItemPerPage(value);
                setCurrentPage(1);
              }}
            />
          )}
          
          <div className="px-6 pb-2">
            <DataAsOfNotice  />
          </div>
        </div>

        {/* Modals */}
        <AddContact
          showModal={showModal}
          onClose={() => setShowModal(false)}
          onCallApi={(filters) => onSuccess(filters, advanceFilterData, includeLatestActivity)}
        />

        <ActivityModal
          showModal={showActivityModal}
          onClose={() => {
            setShowActivityModal(false);
            setIncludeLatestActivity(false); // Reset when modal closes
          }}
          queryParams={buildQueryParams(filterData, includeLatestActivity)}
          advanceFilters={advanceFilterData}
        />

        <PhoneStatusModal
          phone={selectedPhone}
          onClick={closeWidget}
          showModal={showPhoneModal}
          onClose={() => setShowPhoneModal(false)}
        />

        {showColumnModal && (
          <ColumnCustomizer
            allColumns={allColumns}
            visibleColumns={visibleColumns}
            setVisibleColumns={setVisibleColumns}
            storageKey="contactVisibleColumns"
            onClose={() => setShowColumnModal(false)}
          />
        )}
      </div>
    </BaseLayout>
  );
};

export default Contacts;